/* eslint-disable @reatom/unit-naming-rule */
import { atom, onConnect } from '@reatom/framework'

import { Result } from '@blade/std-lib'
import { newTxBufferedEvents } from '@root/entities/realtime/atoms/new-tx-event'
import { fetchTradesRequestAction } from '@root/entities/trade/atoms'
import { reatomResource } from '@root/shared/lib'

import type { Network } from '@root/entities/network'
import type { PairAddress } from '@root/entities/pair/model'
import type { Trade } from '@root/entities/trade'

/**
 * This token's tape: a page of what has happened, then whatever happens next.
 *
 * The app's own trades table does more than this — sorting, cursors, column
 * filters — and it is the right thing where somebody is digging. Here the
 * question is narrower and the answer has to arrive without being asked for,
 * so this is one read and then the socket.
 *
 * The socket already coalesces per animation frame, which is what keeps a
 * token doing forty trades a second from re-rendering forty times.
 */
const PAGE = 40
const KEEP = 120

export const reatomPairTrades = ({
  pairAddress,
  network,
}: {
  pairAddress: PairAddress
  network: Network.Network
}) => {
  const liveAtom = atom<ReadonlyArray<Trade.Trade>>([], 'pairLiveTradesAtom')

  const firstPageAtom = reatomResource(
    async (ctx) => {
      const result = await ctx.schedule(() =>
        fetchTradesRequestAction(ctx, {
          network,
          poolAddress: String(pairAddress),
          sort: 'desc',
          limit: PAGE,
        }),
      )

      return Result.map(result, (page): ReadonlyArray<Trade.Trade> => page.data)
    },
    { name: 'reatomPairFirstTrades', loadOnConnect: true },
  )

  const tradesAtom = atom((ctx) => {
    const live = ctx.spy(liveAtom)
    const first = ctx.spy(firstPageAtom).value ?? []

    const seen = new Set(live.map((trade) => String(trade.id)))

    return [
      ...live,
      ...first.filter((trade) => !seen.has(String(trade.id))),
    ].slice(0, KEEP)
  }, 'pairTradesAtom')

  /*
   * The subscription lives exactly as long as something is reading this atom.
   *
   * It used to be opened where this function runs and never closed, so every
   * token opened left another handler behind, each filtering every trade on
   * the socket for a screen nobody was looking at any more. The app's own
   * trades table has always undone its subscription; this one did not.
   */
  onConnect(tradesAtom, () =>
    newTxBufferedEvents.onCall((ctx, trades) => {
      const mine = trades.filter(
        (trade) => String(trade.pairAddress) === String(pairAddress),
      )
      if (mine.length === 0) {
        return
      }

      // Newest first, and bounded: a tape nobody trims is a tab that slows the
      // whole app down after ten minutes on a busy token.
      liveAtom(ctx, (before) => [...mine.reverse(), ...before].slice(0, KEEP))
    }),
  )

  return tradesAtom
}
